import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import Carousel from '../components/Carousel';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'en-cours', 'termine'

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/projects');
      setProjects(response.data.data || []);
    } catch (err) {
      console.error('Erreur lors du chargement des projets:', err);
      setError('Impossible de charger les projets. Veuillez réessayer plus tard.');
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = filter === 'all'
    ? projects
    : projects.filter(project => project.status === filter);

  const ongoingProjects = projects.filter(p => p.status === 'en-cours');
  // Trie décroissant sur l'année de completion
  const completedProjects = projects
    .filter(p => p.status === 'termine')
    .sort((a, b) => (b.completionDate || 0) - (a.completionDate || 0));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des projets...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Erreur de chargement</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchProjects}
            className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-16 animate-fade-in">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Nos Projets
          </h1>
          <div className="w-20 h-1 bg-primary-600 mx-auto mb-6"></div>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Découvrez nos réalisations les plus récentes et les projets en cours. Nous sommes fiers de partager
            notre expertise et notre passion pour la rénovation immobilière.
          </p>
        </div>

        {/* Filtres */}
        <div className="flex justify-center mb-12 gap-4 flex-wrap">
          <button
            onClick={() => setFilter('all')}
            className={`px-6 py-2 rounded-lg font-semibold transition-colors ${filter === 'all'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
          >
            Tous ({projects.length})
          </button>
          <button
            onClick={() => setFilter('en-cours')}
            className={`px-6 py-2 rounded-lg font-semibold transition-colors ${filter === 'en-cours'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
          >
            En cours ({ongoingProjects.length})
          </button>
          <button
            onClick={() => setFilter('termine')}
            className={`px-6 py-2 rounded-lg font-semibold transition-colors ${filter === 'termine'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
          >
            Terminés ({completedProjects.length})
          </button>
        </div>

        {/* Projets en cours */}
        {(filter === 'all' || filter === 'en-cours') && ongoingProjects.length > 0 && (
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Projets en cours
              <span className="ml-2 text-sm font-normal text-gray-600">
                ({ongoingProjects.length})
              </span>
            </h2>
            <Carousel projects={ongoingProjects} />
          </div>
        )}

        {/* Projets terminés */}
        {(filter === 'all' || filter === 'termine') && completedProjects.length > 0 && (
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Projets récemment terminés
              <span className="ml-2 text-sm font-normal text-gray-600">
                ({completedProjects.length})
              </span>
            </h2>
            <Carousel projects={completedProjects} />
          </div>
        )}

        {/* Message si aucun projet */}
        {filteredProjects.length === 0 && (
          <div className="text-center py-12">
            <svg className="w-24 h-24 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Aucun projet trouvé
            </h3>
            <p className="text-gray-600">
              {filter === 'all'
                ? 'Aucun projet disponible pour le moment.'
                : `Aucun projet ${filter === 'en-cours' ? 'en cours' : 'terminé'} pour le moment.`
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Projects;
